import {
  getCampaignVideoPreviewApiV1,
  getGetCampaignApiV1QueryKey,
  getGetCampaignEngagementApiV1QueryKey,
  submitCampaignYoutubeVideoApiV1,
} from "@/api/__generated__/api.schema";
import { KnownError } from "@/api/client";
import { routes } from "@/constants/url";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useWorkspaceNavigate } from "@/lib/router/use-workspace-navigate";
import { NewTrackingEvent, trackNewEvent } from "@/lib/track/track-event";
import { cn } from "@/lib/utils";
import { YoutubeShortInfo } from "@/routes/campaigns/components/youtube-short-info";
import { refineTimezone } from "@/utils/date";
import { invariant } from "@/utils/invariant";
import { useToastMessage } from "@backpac/monica";
import { Badge, Button, Icon, Input, Loading, Separator, Typography } from "@backpac/monica";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { Temporal } from "temporal-polyfill";

function useFormCampaignId() {
  const { id } = useParams<{ id: string }>();
  invariant(id != null, "Campaign ID is required");

  return id;
}

interface YoutubeShortLinkData {
  shortsUrl: string;
  shortTitle: string;
  shortThumbnailUrl: string;
  shortUploadDate: string;
  usedMusicTrackCoverImageUrl: string;
  usedMusicTrackTitle: string;
  uploadVerification: {
    result: "verified" | "rejected";
    scheduledUploadDate: string;
    realUploadedAt: string;
  };
  trackVerification: {
    result: "verified" | "rejected";
    usedTrackTitle: string;
  };
}

export function CampaignVideoSubmitPage() {
  const toast = useToastMessage();
  const queryClient = useQueryClient();
  const workspaceId = useWorkspaceId();
  const campaignId = useFormCampaignId();
  const navigate = useWorkspaceNavigate();

  const { mutate: submitCampaignYoutubeVideo, isPending } = useMutation({
    mutationFn: ({ videoUrl }: { videoUrl: string }) => {
      try {
        return submitCampaignYoutubeVideoApiV1(workspaceId, campaignId, { videoUrl });
      } catch (error) {
        if (KnownError.is(error)) {
          if (error.status === 400) {
            toast.error("Campaign upload period is invalid");
          }
        } else {
          toast.error("Failed to submit video");
        }
        throw error;
      }
    },
    onSuccess: () => {
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: getGetCampaignApiV1QueryKey(workspaceId, campaignId) }),
        queryClient.invalidateQueries({
          queryKey: getGetCampaignEngagementApiV1QueryKey(workspaceId, campaignId),
        }),
      ]);
    },
  });

  return (
    <div className="h-full px-4 pb-16 desktop:px-0">
      <CampaignVideoSubmitPage.Header
        title="Enter your Short URL"
        description="Paste the link to your YouTube Short video to complete your submission. Make sure the video meets all campaign requirements before submitting."
      />
      <VerifyForm>
        {({ data, isVerifying }) => {
          if (isVerifying) {
            return <Loading />;
          }

          if (data == null) {
            return null;
          }

          return (
            <ConfirmationForm
              className="animate-slide-down-fade"
              data={data}
              cta={
                <Button
                  loading={isPending}
                  onClick={() =>
                    submitCampaignYoutubeVideo(
                      { videoUrl: data.shortsUrl },
                      {
                        onSuccess: () => {
                          toast.info("Video registered successfully");

                          trackNewEvent(NewTrackingEvent.SubmitCampaignVideoUrl, {
                            campaign_id: campaignId,
                            video_url: data.shortsUrl,
                          });

                          navigate(routes.CAMPAIGNS._detail(campaignId));
                        },
                      },
                    )
                  }
                >
                  Submit
                </Button>
              }
            />
          );
        }}
      </VerifyForm>
    </div>
  );
}

interface VerifyYoutubeShortLinkFormValues {
  youtubeShortUrl: string;
}

function VerifyForm({
  defaultValues,
  children,
}: {
  defaultValues?: VerifyYoutubeShortLinkFormValues;
  children: (props: { data: YoutubeShortLinkData; isVerifying: boolean }) => ReactNode;
}) {
  const toast = useToastMessage();
  const workspaceId = useWorkspaceId();
  const campaignId = useFormCampaignId();
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid, isDirty },
  } = useForm<VerifyYoutubeShortLinkFormValues>({
    defaultValues: {
      youtubeShortUrl: defaultValues?.youtubeShortUrl ?? "",
    },
  });
  const {
    mutate: verifyYoutubeShortUrl,
    isPending,
    data: registeredYoutubeShortData,
  } = useMutation({
    mutationFn: async (youtubeShortUrl: string): Promise<YoutubeShortLinkData> => {
      const { data } = await getCampaignVideoPreviewApiV1(workspaceId, campaignId, {
        videoUrl: youtubeShortUrl,
      });
      invariant(data != null, "Invalid video URL");

      const { item } = data;
      const trackTitle =
        item.music == null || item.music.track == null || item.music.artist == null
          ? "Unknown"
          : `${item.music?.track} - ${item.music?.artist}`;

      return {
        shortsUrl: youtubeShortUrl,
        shortTitle: item.title,
        shortThumbnailUrl: item.thumbnailUrl,
        shortUploadDate: item.uploadedAt,
        usedMusicTrackCoverImageUrl: item.music?.imageUrl ?? "",
        usedMusicTrackTitle: trackTitle,
        uploadVerification: {
          result: item.isUploadDateVerified ? "verified" : "rejected",
          scheduledUploadDate: item.estimatedUploadDate,
          realUploadedAt: item.uploadedAt,
        },
        trackVerification: {
          result: item.isTrackVerified ? "verified" : "rejected",
          usedTrackTitle: trackTitle,
        },
      };
    },
    onError: (error) => {
      if (KnownError.is(error) && error.status === 401) {
        toast.error("This video is not from your registered channel");
        return;
      }
      toast.error("Failed to verify video");
    },
  });

  return (
    <div className="space-y-8">
      <form className="flex gap-4" onSubmit={handleSubmit((data) => verifyYoutubeShortUrl(data.youtubeShortUrl))}>
        <div className="flex-1 gap-2">
          <label htmlFor="youtubeShortUrl" className="text-sm">
            YouTube Short URL
          </label>
          <Input
            className="w-full bg-white"
            placeholder="YouTube Short URL"
            {...register("youtubeShortUrl", {
              required: "YouTube Short URL is required",
              validate: validateYoutubeShortUrl,
            })}
          />
          {errors.youtubeShortUrl && <p className="text-red-500 text-sm mt-1">{errors.youtubeShortUrl.message}</p>}
        </div>
        <Button
          className="flex-shrink-0 mt-auto h-[42px]"
          type="submit"
          loading={isSubmitting || isPending}
          disabled={!isValid || !isDirty}
        >
          Verify
        </Button>
      </form>
      {children({
        isVerifying: isSubmitting || isPending,
        data: registeredYoutubeShortData!,
      })}
    </div>
  );
}

interface VerifyYoutubeShortLinkConfirmFormProps {
  cta: ReactNode;
  className?: string;
  data: YoutubeShortLinkData;
}

function ConfirmationForm({ cta, className, data }: VerifyYoutubeShortLinkConfirmFormProps) {
  const {
    shortTitle,
    shortThumbnailUrl,
    shortUploadDate,
    usedMusicTrackCoverImageUrl,
    usedMusicTrackTitle,
    uploadVerification,
    trackVerification,
  } = data;
  const notVerified = uploadVerification.result === "rejected" || trackVerification.result === "rejected";

  return (
    <div className={cn("space-y-4", className)}>
      <Typography asChild font="unbounded" size="xl">
        <h2>Is this the correct Short for the campaign?</h2>
      </Typography>
      <div className="flex flex-col gap-4 border border-borderLight rounded-[12px] p-4 bg-white">
        <YoutubeShortInfo
          shortTitle={shortTitle}
          shortThumbnailUrl={shortThumbnailUrl}
          shortUploadDate={shortUploadDate}
          usedMusicTrackCoverImageUrl={usedMusicTrackCoverImageUrl}
          usedMusicTrackTitle={usedMusicTrackTitle}
        />
        <Separator />
        <VerificationResults uploadVerification={uploadVerification} trackVerification={trackVerification} />
      </div>
      {notVerified && (
        <Typography size="paragraphXs" color="textMuted" className="italic">
          Upload date or track usage did not meet requrements. You can still submit, but your participation may not be
          eligible for the campaign.
        </Typography>
      )}
      <div className="flex pt-4 justify-end">{cta}</div>
    </div>
  );
}

interface VerificationResultProps {
  uploadVerification: {
    result: "verified" | "rejected";
    scheduledUploadDate: string;
    realUploadedAt: string;
  };
  trackVerification: {
    result: "verified" | "rejected";
    usedTrackTitle: string;
  };
}

function VerificationResults({ uploadVerification, trackVerification }: VerificationResultProps) {
  return (
    <div className="relative pl-[16px]">
      <div className="absolute left-8 top-8 bottom-8 w-px bg-[#e5e5e5]" />
      <div className="flex gap-4">
        <div className="relative z-1">
          <Icon name="calendar" bgColor="colorBlueLight" className="p-2 text-colorBlue" iconSize="16px" />
        </div>
        <div className="flex-1">
          <div className="flex flex-col desktop:flex-row items-start desktop:items-center justify-start desktop:justify-between gap-1 mb-3">
            <Typography size="sm" weight={600}>
              Upload date verification
            </Typography>
            {
              {
                rejected: <Badge theme="red">Upload date not met</Badge>,
                verified: <Badge theme="green">Verified</Badge>,
              }[uploadVerification.result]
            }
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-1">
              <Typography size="xs">Scheduled upload date:</Typography>
              <Typography size="xs">
                {refineTimezone(
                  Temporal.PlainDate.from(uploadVerification.scheduledUploadDate).toLocaleString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  }),
                )}
                {", "}
                00:00 - 23:59 (PDT)
              </Typography>
            </div>
            <div className="flex items-center justify-between gap-1">
              <Typography size="xs">Upload on:</Typography>
              <Typography size="xs">
                {refineTimezone(
                  Temporal.Instant.from(uploadVerification.realUploadedAt)
                    .toZonedDateTime({ timeZone: "America/Los_Angeles", calendar: "iso8601" })
                    .toLocaleString("en-US", {
                      month: "long",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      hour12: false,
                      timeZoneName: "long",
                    }),
                )}
              </Typography>
            </div>
          </div>
        </div>
      </div>
      <div className="pl-12">
        <Separator className="my-4" />
      </div>
      <div className="flex gap-4">
        <div className="relative z-1">
          <Icon name="music" bgColor="colorOrangeLight" className="p-2 text-colorOrange" iconSize="16px" />
        </div>
        <div className="flex-1">
          <div className="flex flex-col desktop:flex-row items-start desktop:items-center justify-start desktop:justify-between gap-1 mb-3">
            <Typography size="sm" weight={600}>
              Track verification
            </Typography>
            {
              {
                rejected: <Badge theme="red">Not used</Badge>,
                verified: <Badge theme="green">Verified</Badge>,
              }[trackVerification.result]
            }
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between gap-1">
              <Typography size="xs">Track used:</Typography>
              <Typography size="xs">{trackVerification.usedTrackTitle}</Typography>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const validateYoutubeShortUrl = (value: string) => {
  const youtubeShortRegex =
    /^(https?:\/\/)?(www\.)?(youtube\.com\/shorts\/|youtube\.com\/.*\?.*shortsid=|youtu\.be\/)([\w-]{11})(\?.*)?$/i;

  return youtubeShortRegex.test(value) || "Please enter a valid YouTube Shorts URL";
};

CampaignVideoSubmitPage.Header = function Header({ title, description }: { title: string; description: string }) {
  return (
    <div className="space-y-3 mb-8">
      <Typography asChild font="unbounded" size="xl">
        <h1>{title}</h1>
      </Typography>
      <Typography size="paragraphXs" color="textMuted">
        {description}
      </Typography>
    </div>
  );
};
